if (!customElements.get('wls-custom-dropdown')) {
  class WLSCustomDropdown extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        dropdownBox: 'js-dropdown-box',
        dropdownButton: 'js-dropdown-button',
        dropdownList: 'js-dropdown-list',
        dropdownItem: 'js-dropdown-item',
        dropdownPlaceholder: 'js-dropdown-placeholder',
        dropdownItemContent: 'js-dropdown-item-content',
        dropdownLabel: 'js-dropdown-label',
        selected: 'selected',
        hidden: 'hidden',
      };

      this.selectors = this.getSelectors();
      this.isOpen = false;
      this.observer = null;
    }

    getSelectors() {
      return {
        dropdownBox: this.querySelector(`.${this.classes.dropdownBox}`),
        dropdownButton: this.querySelector(`.${this.classes.dropdownButton}`),
        dropdownList: this.querySelector(`.${this.classes.dropdownList}`),
        dropdownItems: this.querySelectorAll(`.${this.classes.dropdownItem}`),
        dropdownPlaceholder: this.querySelector(`.${this.classes.dropdownPlaceholder}`),
        dropdownLabel: this.querySelector(`.${this.classes.dropdownLabel}`),
      };
    }

    connectedCallback() {
      this.setupEventListeners();
      this.setupMutationObserver();
      this.updatePlaceholderFromSelectedItem();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    setupMutationObserver() {
      // Create a mutation observer to watch for changes in the dropdown items
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            this.updatePlaceholderFromSelectedItem();
          }
        });
      });

      // Observe all dropdown items for class changes
      this.selectors.dropdownItems.forEach((item) => {
        this.observer.observe(item, {
          attributes: true,
          attributeFilter: ['class'],
        });
      });
    }

    updatePlaceholderFromSelectedItem() {
      const selectedItem = this.querySelector(`.${this.classes.dropdownItem}.${this.classes.selected}`);
      const selectedContent = selectedItem?.querySelector(`.${this.classes.dropdownItemContent}`);

      if (selectedContent && this.selectors.dropdownPlaceholder) {
        // Clone the content
        const clonedContent = selectedContent.cloneNode(true);

        // Clear existing placeholder content
        this.selectors.dropdownPlaceholder.innerHTML = '';

        // Add the cloned content
        this.selectors.dropdownPlaceholder.appendChild(clonedContent);

        // Hide the label and show the placeholder
        if (this.selectors.dropdownLabel) {
          this.selectors.dropdownLabel.classList.add(this.classes.hidden);
        }
        this.selectors.dropdownPlaceholder.classList.remove(this.classes.hidden);
      } else {
        // If no selection, show label and hide placeholder
        if (this.selectors.dropdownLabel) {
          this.selectors.dropdownLabel.classList.remove(this.classes.hidden);
        }
        if (this.selectors.dropdownPlaceholder) {
          this.selectors.dropdownPlaceholder.classList.add(this.classes.hidden);
        }
      }
    }

    setupEventListeners() {
      if (!this.selectors.dropdownBox || !this.selectors.dropdownList) return;

      // Toggle dropdown on button click
      this.selectors.dropdownButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleDropdown();
      });

      // Handle clicks on dropdown items
      this.selectors.dropdownItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleItemInteraction(item);
        });

        // Handle any input changes within items
        const input = item.querySelector('input');
        if (input) {
          input.addEventListener('change', () => {
            // Remove selected class from all items
            this.querySelectorAll(`.${this.classes.dropdownItem}`).forEach((dropdownItem) => {
              dropdownItem.classList.remove(this.classes.selected);
            });
            // Add selected class to the parent dropdown item
            item.classList.add(this.classes.selected);
            this.closeDropdown();
          });
        }

        // Handle any link clicks within items
        const link = item.querySelector('a');
        if (link) {
          link.addEventListener('click', () => {
            // Remove selected class from all items
            this.querySelectorAll(`.${this.classes.dropdownItem}`).forEach((dropdownItem) => {
              dropdownItem.classList.remove(this.classes.selected);
            });
            // Add selected class to the parent dropdown item
            item.classList.add(this.classes.selected);
            this.closeDropdown();
          });
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeDropdown();
        }
      });

      // Handle keyboard navigation
      this.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
    }

    toggleDropdown() {
      this.isOpen ? this.closeDropdown() : this.openDropdown();
    }

    openDropdown() {
      this.isOpen = true;
      this.classList.add(this.classes.active);
      this.selectors.dropdownList.classList.add(this.classes.active);
      this.selectors.dropdownBox.setAttribute('aria-expanded', 'true');
      this.selectors.dropdownList.setAttribute('aria-hidden', 'false');

      // Focus the first interactive element
      const firstInteractive = this.selectors.dropdownList.querySelector('input, a');
      if (firstInteractive) {
        firstInteractive.focus();
      }
    }

    closeDropdown() {
      this.isOpen = false;
      this.classList.remove(this.classes.active);
      this.selectors.dropdownList.classList.remove(this.classes.active);
      this.selectors.dropdownBox.setAttribute('aria-expanded', 'false');
      this.selectors.dropdownList.setAttribute('aria-hidden', 'true');
    }

    handleItemInteraction(item) {
      const input = item.querySelector('input');
      const link = item.querySelector('a');

      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (link) {
        link.click();
      }

      this.closeDropdown();
    }

    handleKeyboardNavigation(event) {
      if (!this.isOpen) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openDropdown();
        }
        return;
      }

      switch (event.key) {
        case 'Escape':
          this.closeDropdown();
          this.selectors.dropdownButton.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.focusNextItem();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focusPreviousItem();
          break;
      }
    }

    focusNextItem() {
      const focusableElements = this.getFocusableElements();
      const currentIndex = focusableElements.indexOf(document.activeElement);
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      focusableElements[nextIndex].focus();
    }

    focusPreviousItem() {
      const focusableElements = this.getFocusableElements();
      const currentIndex = focusableElements.indexOf(document.activeElement);
      const previousIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
      focusableElements[previousIndex].focus();
    }

    getFocusableElements() {
      return Array.from(this.querySelectorAll('input, a, button'));
    }
  }

  window.customElements.define('wls-custom-dropdown', WLSCustomDropdown);
}
